import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  RohsComponentComplianceFileDetailView,
  RohsComponentEvidenceSlot,
} from '@/shared/hooks/api/rohsComplianceFiles'
import { useComplianceActorDisplayLabel } from '@/shared/hooks/useComplianceActorDisplayLabel'
import { getRohsComponentSlotFileName } from '@/shared/lib/getRohsComponentSlotFileName'
import type { ComponentRohsBriefingStep } from '@/shared/briefing/buildComponentRohsBriefing'
import { Button } from '@/components/ui/button'
import { V2RohsPartDrawingsGuidance } from '@/v2/components/V2RohsPartDrawingsGuidance'
import { V2RohsSharedEvidenceFileItem } from '@/v2/components/rohs-legislation-briefing/V2RohsSharedEvidenceFileItem'
import { V2RohsTestReportGuidance } from '@/v2/components/V2RohsTestReportGuidance'
import { V2ComponentRohsBriefingStepShell } from './V2ComponentRohsBriefingStepShell'
import { V2ComponentRohsComplianceSlotFileDialog } from './V2ComponentRohsComplianceSlotFileDialog'

export function V2ComponentRohsComplianceSlotStep({
  step,
  index,
  componentId,
  file,
  slotKind,
  canWrite,
}: {
  step: ComponentRohsBriefingStep
  index: number
  componentId: string
  file: RohsComponentComplianceFileDetailView
  slotKind: RohsComponentEvidenceSlot
  canWrite: boolean
}) {
  const { t } = useTranslation('common')
  const [dialogOpen, setDialogOpen] = useState(false)
  const fileName = getRohsComponentSlotFileName(file, slotKind)
  const hasFile = fileName != null
  const slot = file.slots.find((item) => item.slotKind === slotKind)
  const uploadedByActor = slot?.uploadedByActor ?? null
  const actorLabel = useComplianceActorDisplayLabel(uploadedByActor)

  let actions
  if (canWrite && !hasFile) {
    actions = (
      <Button
        type='button'
        variant='outline'
        size='sm'
        onClick={() => {
          setDialogOpen(true)
        }}
      >
        {t('v2.pages.products.detail.rohsFiles.componentFile.actions.upload')}
      </Button>
    )
  }

  return (
    <>
      <V2ComponentRohsBriefingStepShell
        step={step}
        index={index}
        actions={actions}
      >
        {slotKind === 'TEST_REPORT' ? <V2RohsTestReportGuidance /> : undefined}
        {slotKind === 'DRAWINGS_SCHEMAS' ? (
          <V2RohsPartDrawingsGuidance />
        ) : undefined}
        {hasFile ? (
          <div className='space-y-1'>
            <V2RohsSharedEvidenceFileItem
              fileName={fileName}
              canWrite={canWrite}
              onEdit={() => {
                setDialogOpen(true)
              }}
            />
            {uploadedByActor != null ? (
              <p className='text-xs text-muted-foreground'>
                {t('v2.pages.products.detail.rohsFiles.componentFile.uploadedBy', {
                  actor: actorLabel,
                })}
              </p>
            ) : null}
          </div>
        ) : undefined}
      </V2ComponentRohsBriefingStepShell>
      {canWrite ? (
        <V2ComponentRohsComplianceSlotFileDialog
          componentId={componentId}
          slotKind={slotKind}
          fileName={fileName}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
        />
      ) : null}
    </>
  )
}
